import {
  createContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useBreadcrumbsContext } from 'app/context';
import { IApplicationCustomVariablesValues } from 'app/interfaces/customVariablesInterfaces';
import { Stepper } from 'app/components';
import { TSubscriber } from './SubscribersStep/SubscribersAddNewSideSheet';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import { TProducts } from 'modules/products/context';
import {
  ApplicationRequestContainer,
  ApplicationRequestStepContainer,
  ApplicationRequestStepperContainer,
} from './styles';
import { TApplicationResponse } from 'modules/products/services/hooks/interfaces';
import {
  useApplicationsService,
  useProductsService,
} from 'modules/products/services/hooks';
import {
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { TQualification } from 'modules/customer/interfaces/qualifications';
import { useAuthContext } from 'modules/auth/context';
import { TBeneficiaryAccount } from 'modules/products/interfaces/beneficiaryAccount';
import useCreditSimulatorV2 from 'app/hooks/useCreditSimulatorV2';
import { TCustodian } from 'modules/products/interfaces/custodian';
import { BRAZILIAN_STATES_OBJECT } from 'app/helpers/addressHelper';
import { ValidateBilletsResponse } from 'modules/products/interfaces/billets';
import {
  EBeneficiaryType,
  TypeProductEmission,
} from 'modules/products/context/ProductsProvider/products.interfaces';
import { useAppContext } from 'app/context/AppContextProvider';
import { useOriginatorContext } from 'modules/common/OriginatorProvider';
import { ValidateEmvResponse } from 'modules/products/interfaces/qrCode';
import {
  IConsigned,
  IConsignedList,
  IConsignedProduct,
} from './ConsignedStep/interfaces';
import { formatCurrency } from 'app/utils/normalizer';
import {
  ESplitPaymentAmountType,
  ESplitPaymentType,
  SplitPayment,
} from 'modules/products/interfaces/splitPayment';
import {
  SimulatorV2HttpService,
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';
import { SimulationV2Step } from './SimulationV2Step';
import {
  IElegibility,
  IElegibilityConfigs,
  ISimulationResponse,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2/types';
import { BoxIcon } from 'app/components/Icons';
import {
  getApplicationSteps,
  SIMULATION_V2_STEP_TITLE,
} from './applicationSteps';
import { IFgtsPeriodsReview } from './FgtsStep/interfaces';
import {
  TLoanGuaranteeFormValues,
  TLoanGuaranteeInfo,
  defaultLoanGuaranteeFormValues,
  mergeLoanGuaranteeInfoFromConsignedGuaranteeInfo,
} from './ConsultaFgts/interfaces';
import { format } from 'date-fns';

export type IApplicationRequestContext = {
  activeStep: number;
  onForward: () => void;
  onBack: () => void;
  onBackTwoSteps: () => void;
  hasVariablesStep: boolean;

  hasCustomVariables?: boolean;
  setHasCustomVariables: (hasCustomVariables: boolean) => void;

  borrowerData:
    | (TRegisterPersonDataRequest & TRegisterBusinessDataRequest)
    | null;
  setBorrowerData: (
    borrower:
      | (TRegisterPersonDataRequest & TRegisterBusinessDataRequest)
      | null,
  ) => void;

  borrowerQualification: TQualification | null;
  setBorrowerQualification: (qualification: TQualification | null) => void;

  subscribers: TSubscriber[] | null;
  setSubscribers: (subscribers: TSubscriber[] | null) => void;

  product: TProducts | null;
  setProduct: (product: TProducts) => void;

  funding: string | null;
  hasMultiplesFundings: boolean;
  setFunding: (funding: string) => void;

  beneficiaryAccount: TBeneficiaryAccount | null;
  setBeneficiaryAccount: (beneficiaryAccount: TBeneficiaryAccount) => void;

  customVariables: IApplicationCustomVariablesValues[] | null;
  setCustomVariables: (variables: IApplicationCustomVariablesValues[]) => void;

  custodian: TCustodian | null;
  setCustodian: (custodian: TCustodian) => void;

  customCcbTemplateId: string | null;
  setCustomCcbTemplateId: (customCcbTemplateId: string | null) => void;

  basePremiumAmount: number | null;

  simulationV2: TSimulatorV2Response | null;
  simulationV2Request: TSimulatorV2Request | null;
  setSimulationV2: (
    simulation: TSimulatorV2Response | null,
    simulationRequest: TSimulatorV2Request | null,
    tabIndex: string,
  ) => void;

  simulateWithDifferentDisbursementDate: (disbursementDate: string) => void;

  billetValidationResponse: ValidateBilletsResponse | null;
  setBilletValidationResponse: (
    billetsResponse: ValidateBilletsResponse | null,
  ) => void;

  qrcodeEmvValidationResponse: ValidateEmvResponse | null;
  setQrcodeEmvValidationResponse: (
    emvResponse: ValidateEmvResponse | null,
  ) => void;

  originator: string | null;
  updateOriginator: (originator: string | null) => void;

  splitPayments: SplitPayment[];
  setSplitPayments: (splitPayments: SplitPayment[]) => void;

  isClonning: TApplicationResponse | null;

  consignedError: string;
  consignedProduct?: IConsignedProduct;
  setConsignedProduct: (consigned?: IConsignedProduct) => void;
  consignedConfig?: IConsigned;
  setConsignedConfig: (value?: IConsigned) => void;
  consignedList?: IConsignedList;
  setConsignedList: (value?: IConsignedList) => void;
  fgtsPeriods?: IFgtsPeriodsReview[];
  setFgtsPeriods: (value?: IFgtsPeriodsReview[]) => void;
  fgtsValidationErrors: boolean;
  setFgtsValidationErrors: (hasErrors: boolean) => void;
  fgtsQueryBalanceCheckId?: string;
  setFgtsQueryBalanceCheckId: (balanceCheckId?: string) => void;
  loanGuaranteeInfo?: TLoanGuaranteeInfo;
  setLoanGuaranteeInfo: (
    value:
      | TLoanGuaranteeInfo
      | undefined
      | ((
          prev: TLoanGuaranteeInfo | undefined,
        ) => TLoanGuaranteeInfo | undefined),
  ) => void;
  loanGuaranteeFormValues: TLoanGuaranteeFormValues;
  setLoanGuaranteeFormValues: (
    value:
      | TLoanGuaranteeFormValues
      | ((prev: TLoanGuaranteeFormValues) => TLoanGuaranteeFormValues),
  ) => void;
  simulationPersistedNumPayments: string | null;
  setSimulationPersistedNumPayments: (value: string | null) => void;
  setConsignedError: (error: string) => void;
  clearProductSelection: () => void;

  cobransaasResponse?: ISimulationResponse;
  setCobransaasResponse: (cobransaasResponse?: ISimulationResponse) => void;

  tacValidationError: string;
  setTacValidationError: (error: string) => void;

  getElegibilityRules: (productId: string) => Promise<IElegibility | null>;
  setElegibilityError: ({
    message,
    description,
  }: {
    message: string;
    description: string;
  }) => void;
  elegibilityError: { message: string; description: string };
  elegibilities?: IElegibilityConfigs;
  setElegibilities: (configs: IElegibilityConfigs) => void;
};

export const ApplicationRequestContext =
  createContext<IApplicationRequestContext>({
    activeStep: 1,
    hasVariablesStep: false,
    hasCustomVariables: false,
    isClonning: null,

    setHasCustomVariables: () => {
      throw new Error('Método não implementado');
    },

    onForward: () => {
      throw new Error('Método não implementado');
    },
    onBack: () => {
      throw new Error('Método não implementado');
    },
    onBackTwoSteps: () => {
      throw new Error('Método não implementado');
    },

    beneficiaryAccount: null,
    setBeneficiaryAccount: () => {
      throw new Error('Método não implementado');
    },

    funding: null,
    hasMultiplesFundings: false,
    setFunding: () => {
      throw new Error('Método não implementado');
    },

    product: null,
    setProduct: () => {
      throw new Error('Método não implementado');
    },

    customVariables: null,
    setCustomVariables: () => {
      throw new Error('Método não implementado');
    },

    borrowerData: null,
    setBorrowerData: () => {
      throw new Error('Método não implementado');
    },

    borrowerQualification: null,
    setBorrowerQualification: () => {
      throw new Error('Método não implementado');
    },

    subscribers: null,
    setSubscribers: () => {
      throw new Error('Método não implementado');
    },

    custodian: null,
    setCustodian: () => {
      throw new Error('Método não implementado');
    },

    basePremiumAmount: null,

    simulationV2: null,
    simulationV2Request: null,
    setSimulationV2: () => {
      throw new Error('Método não implementado');
    },

    simulateWithDifferentDisbursementDate: () => {
      throw new Error('Método não implementado');
    },

    billetValidationResponse: null,
    setBilletValidationResponse: () => {
      throw new Error('Método não implementado');
    },

    qrcodeEmvValidationResponse: null,
    setQrcodeEmvValidationResponse: () => {
      throw new Error('Método não implementado');
    },

    originator: null,
    updateOriginator: () => {
      throw new Error('Método não implementado');
    },

    consignedProduct: undefined,
    setConsignedProduct: () => {
      throw new Error('Método não implementado');
    },
    setConsignedConfig: () => {
      throw new Error('Método não implementado');
    },
    consignedError: '',
    setConsignedError: () => {
      throw new Error('Método não implementado');
    },
    consignedConfig: undefined,
    consignedList: undefined,
    fgtsPeriods: undefined,
    setFgtsPeriods: () => {
      throw new Error('Método não implementado');
    },
    fgtsValidationErrors: false,
    setFgtsValidationErrors: () => {
      throw new Error('Método não implementado');
    },
    fgtsQueryBalanceCheckId: undefined,
    setFgtsQueryBalanceCheckId: () => {
      throw new Error('Método não implementado');
    },
    loanGuaranteeInfo: undefined,
    setLoanGuaranteeInfo: () => {
      throw new Error('Método não implementado');
    },
    loanGuaranteeFormValues: defaultLoanGuaranteeFormValues,
    setLoanGuaranteeFormValues: () => {
      throw new Error('Método não implementado');
    },
    simulationPersistedNumPayments: null,
    setSimulationPersistedNumPayments: () => {
      throw new Error('Método não implementado');
    },
    setConsignedList: () => {
      throw new Error('Método não implementado');
    },

    splitPayments: [],
    setSplitPayments: () => {
      throw new Error('Método não implementado');
    },

    clearProductSelection: () => {
      throw new Error('Método não implementado');
    },

    cobransaasResponse: undefined,
    setCobransaasResponse: () => {
      throw new Error('Método não implementado');
    },

    customCcbTemplateId: null,
    setCustomCcbTemplateId: () => {
      throw new Error('Método não implementado');
    },
    setTacValidationError: () => {
      throw new Error('Método não implementado');
    },
    tacValidationError: '',
    getElegibilityRules: () => {
      throw new Error('Método não implementado');
    },
    elegibilityError: { message: '', description: '' },
    setElegibilityError: () => {
      throw new Error('Método não implementado');
    },
    elegibilities: undefined,
    setElegibilities: () => {
      throw new Error('Método não implementado');
    },
  });

export enum TacValueType {
  PERCENTAGE = 'PERCENTAGE',
  MONETARY = 'MONETARY',
}

const ApplicationRequest = () => {
  // Hooks
  const theme = useTheme();
  const { setItems, resetBreadcrumbs } = useBreadcrumbsContext();
  const { is_issuer_app, feature_flags } = useAppContext();
  const { selectedOriginator, setSelectedOriginator } = useOriginatorContext();
  const { getPerson } = usePersonService();
  const { getBusiness } = useCustomerService();
  const { getProduct } = useProductsService();
  const { simulate: simulateV2 } = useCreditSimulatorV2();
  const { getBasePremiumAmount } = useApplicationsService();
  const { userInfo } = useAuthContext();
  const location = useLocation();

  // States
  const [cobransaasResponse, setCobransaasResponse] =
    useState<ISimulationResponse>();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [product, setProduct] = useState<TProducts | null>(null);
  const [hasCustomVariables, setHasCustomVariables] = useState(false);
  const [billetValidationResponse, setBilletValidationResponse] =
    useState<ValidateBilletsResponse | null>(null);
  const [qrcodeEmvValidationResponse, setQrcodeEmvValidationResponse] =
    useState<ValidateEmvResponse | null>(null);
  const [isClonning, setIsClonning] = useState<TApplicationResponse | null>(
    null,
  );
  const [splitPayments, setSplitPayments] = useState<SplitPayment[]>([]);
  const [beneficiaryAccount, setBeneficiaryAccount] =
    useState<TBeneficiaryAccount | null>(null);
  const [borrowerData, setBorrowerData] = useState<
    (TRegisterPersonDataRequest & TRegisterBusinessDataRequest) | null
  >(null);
  const [borrowerQualification, setBorrowerQualification] =
    useState<TQualification | null>(null);
  const [custodian, setCustodian] = useState<TCustodian | null>(null);

  const [simulationV2, setSimulationV2] = useState<TSimulatorV2Response | null>(
    null,
  );
  const [simulationV2Request, setSimulationV2Request] =
    useState<TSimulatorV2Request | null>(null);
  const [subscribers, setSubscribers] = useState<TSubscriber[] | null>(null);
  const [basePremiumAmount, setBasePremiumAmount] = useState<number | null>(
    null,
  );
  const [tabSimulationIndex, setTabSimulationIndex] = useState<string | null>(
    null,
  );
  const [customVariables, setCustomVariables] = useState<
    IApplicationCustomVariablesValues[] | null
  >(null);
  const [selectedFunding, setSelectedFunding] = useState<string | null>(null);

  const [consignedError, setConsignedError] = useState<string>(
    'CPF não disponível para consulta neste produto',
  );
  const [consignedProduct, setConsignedProduct] = useState<IConsignedProduct>();
  const [consignedConfig, setConsignedConfig] = useState<IConsigned>();
  const [consignedList, setConsignedList] = useState<IConsignedList>();
  const [fgtsPeriods, setFgtsPeriods] = useState<IFgtsPeriodsReview[]>();
  const [fgtsValidationErrors, setFgtsValidationErrors] = useState(false);
  const [fgtsQueryBalanceCheckId, setFgtsQueryBalanceCheckId] = useState<
    string | undefined
  >();
  const [loanGuaranteeInfo, setLoanGuaranteeInfo] =
    useState<TLoanGuaranteeInfo>();
  const [loanGuaranteeFormValues, setLoanGuaranteeFormValues] =
    useState<TLoanGuaranteeFormValues>(defaultLoanGuaranteeFormValues);
  const [simulationPersistedNumPayments, setSimulationPersistedNumPayments] =
    useState<string | null>(null);
  const [customCcbTemplateId, setCustomCcbTemplateId] = useState<string | null>(
    null,
  );
  const [tacValidationError, setTacValidationError] = useState('');

  const [elegibilities, setElegibilities] = useState<IElegibilityConfigs>();
  const [elegibilityError, setElegibilityError] = useState<{
    message: string;
    description: string;
  }>({ message: '', description: '' });

  // Variables
  const isConsigned = !!product?.organ_type;

  const prevProduct = useRef<TProducts | null>(
    typeof location.state?.product === 'object'
      ? location.state?.product
      : null,
  );

  const hasMultiplesFundings = useMemo(
    () => userInfo && userInfo.fundings.length > 1,
    [userInfo],
  );

  const isNewSimulator = useMemo(() => {
    return feature_flags?.NEW_SIMULATOR_ENABLED
      ? feature_flags?.NEW_SIMULATOR_ENABLED?.isActive
      : false;
  }, [feature_flags]);

  const hasBorrower = useMemo(() => {
    if (typeof location.state?.person === 'object') {
      history.replaceState({}, document.title);
      return location.state?.person;
    }
    if (typeof location.state?.business === 'object') {
      history.replaceState({}, document.title);
      return location.state?.business;
    }
    return null;
  }, [location]);

  const hasApplication = useMemo<TApplicationResponse | null>(() => {
    if (typeof location.state?.application === 'object') {
      history.replaceState({}, document.title);
      return location.state?.application;
    }
    return null;
  }, [location]);

  const hasCreatedBorrower = useMemo(() => {
    if (typeof location.state?.borrower === 'object') {
      history.replaceState({}, document.title);
      return {
        product: location.state?.product,
        borrower: location.state?.borrower,
      };
    }
    return null;
  }, [location]);

  // Handlers
  const clearProductSelection = () => {
    setConsignedProduct(undefined);
    setConsignedConfig(undefined);
    setConsignedList(undefined);
    setFgtsQueryBalanceCheckId(undefined);
    setFgtsPeriods(undefined);
    setFgtsValidationErrors(false);
    setLoanGuaranteeInfo(undefined);
    setLoanGuaranteeFormValues(defaultLoanGuaranteeFormValues);
    setSimulationPersistedNumPayments(null);
  };

  const getElegibilityRules = async (productId: string) => {
    try {
      const simulatorService = new SimulatorV2HttpService();
      const res = await simulatorService.getElegibilityRules(productId);

      if (res) {
        setElegibilities(res.configs);
        return res;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const guaranteeInfo = consignedConfig?.meta_data?.guarantee_info;

    if (!guaranteeInfo) {
      return;
    }

    setLoanGuaranteeInfo((prev) =>
      mergeLoanGuaranteeInfoFromConsignedGuaranteeInfo(prev, guaranteeInfo),
    );
  }, [consignedConfig]);

  useEffect(() => {
    setItems(
      [
        { to: '/products/main', label: 'Produtos' },
        {
          to: product?.id
            ? `/products/detail/${product?.id}/applications`
            : '/products/main',
          label: product?.name
            ? `${product?.name} / Criar solicitação`
            : 'Criar solicitação',
        },
      ],
      <BoxIcon color={theme.palette.text.secondary} />,
    );

    return () => resetBreadcrumbs();
  }, [product]);

  useEffect(() => {
    if (hasCreatedBorrower !== null) {
      if (
        typeof hasCreatedBorrower.product === 'object' &&
        typeof hasCreatedBorrower.borrower === 'object'
      ) {
        setBorrowerData(hasCreatedBorrower.borrower);
        setProduct(hasCreatedBorrower.product);
        setActiveStep(2);
      }
    }
  }, [hasCreatedBorrower]);

  useEffect(() => {
    if (typeof hasBorrower === 'object' && hasBorrower !== null) {
      setBorrowerData(hasBorrower);
    }
  }, [hasBorrower]);

  useEffect(() => {
    if (hasApplication !== null) {
      setIsClonning(hasApplication);

      if (hasApplication.originator?.id) {
        setSelectedOriginator(hasApplication.originator.id);
      }
      getProduct(hasApplication.product.id).then((productDetails) => {
        const productData = productDetails?.data as TProducts;
        setProduct(productData);

        if (
          productData?.type_product_emission ===
            TypeProductEmission.COMMERCIAL_PAPER &&
          hasApplication.custodian
        ) {
          setCustodian(() => ({
            taxpayer_id: hasApplication.custodian!.taxpayer_id,
            legal_name: hasApplication.custodian!.legal_name,
            phone: {
              country_code: hasApplication.custodian!.phone.country_code,
              area_code: '',
              number: `${hasApplication.custodian!.phone.area_code}${
                hasApplication.custodian!.phone.number
              }`,
            },
            foundation_date: hasApplication.custodian!.foundation_date,
            email_address: hasApplication.custodian!.email_address,
            address: {
              city: {
                label: hasApplication.custodian!.address.city,
                value: hasApplication.custodian!.address.city,
              },
              country_code: {
                label: hasApplication.custodian!.address.country_code,
                value: hasApplication.custodian!.address.country_code,
              },
              district: hasApplication.custodian!.address.district,
              extra_info: hasApplication.custodian!.address.extra_info,
              postal_code: hasApplication.custodian!.address.postal_code,
              state_code: {
                label:
                  BRAZILIAN_STATES_OBJECT[
                    hasApplication.custodian!.address.state_code
                  ],
                value: hasApplication.custodian!.address.state_code,
              },
              street_name: hasApplication.custodian!.address.street_name,
              street_number: String(
                hasApplication.custodian!.address.street_number,
              ),
            },
          }));
        }

        if (
          productData?.beneficiary_type ===
            EBeneficiaryType.EXTERNAL_REGISTERED_ACCOUNT &&
          hasApplication?.beneficiary_account
        ) {
          setBeneficiaryAccount(() => hasApplication!.beneficiary_account!);
        }

        let borrowerDataInternal: TRegisterPersonDataRequest &
          TRegisterBusinessDataRequest;
        if (productData.borrower_type === 'PERSON') {
          getPerson(hasApplication.borrower.id).then((personDetails) => {
            borrowerDataInternal = personDetails as TRegisterPersonDataRequest &
              TRegisterBusinessDataRequest;
            setBorrowerData(
              personDetails as TRegisterPersonDataRequest &
                TRegisterBusinessDataRequest,
            );
          });
        } else {
          getBusiness(hasApplication.borrower.id).then((personDetails) => {
            borrowerDataInternal = personDetails as TRegisterPersonDataRequest &
              TRegisterBusinessDataRequest;
            setBorrowerData(
              personDetails as TRegisterPersonDataRequest &
                TRegisterBusinessDataRequest,
            );
          });
        }

        if (
          productData?.beneficiary_type ===
            EBeneficiaryType.SPLIT_DISBURSEMENT &&
          hasApplication?.split_beneficiary_account
        ) {
          const amountType = hasApplication.split_beneficiary_account!
            .percentage
            ? ESplitPaymentAmountType.PERCENT
            : ESplitPaymentAmountType.MONEY;

          const borrowerAmount =
            amountType === ESplitPaymentAmountType.PERCENT
              ? 100 - hasApplication.split_beneficiary_account!.percentage!
              : hasApplication.requested_amount -
                hasApplication.split_beneficiary_account!.amount!;
          setSplitPayments(() => [
            {
              type: ESplitPaymentType.BORROWER,
              taxpayer_id: borrowerDataInternal!.taxpayer_id,
              name: borrowerDataInternal!.full_name,
              external_bank_account:
                borrowerDataInternal!.external_bank_account,
              pix: borrowerDataInternal!.pix,
              amount: borrowerAmount,
              amountString:
                amountType === ESplitPaymentAmountType.PERCENT
                  ? `${borrowerAmount}%`
                  : formatCurrency(borrowerAmount),
              amount_type: amountType,
            },
            {
              name: hasApplication.split_beneficiary_account!.holder.name,
              taxpayer_id:
                hasApplication.split_beneficiary_account!.holder.taxpayer_id,
              amount: hasApplication.split_beneficiary_account!.amount!,
              amountString:
                amountType === ESplitPaymentAmountType.PERCENT
                  ? `${hasApplication.split_beneficiary_account!.amount}%`
                  : formatCurrency(
                      hasApplication.split_beneficiary_account!.amount,
                    ),
              amount_type: amountType,
              external_bank_account: hasApplication.split_beneficiary_account!
                .external_bank_account
                ? {
                    bank_account:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.bank_account,
                    bank_code:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.bank_code,
                    bank_branch:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.bank_branch,
                    bank_account_digit:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.bank_account_digit,
                    ispb_code:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.ispb_code,
                    bank_account_type:
                      hasApplication.split_beneficiary_account!
                        .external_bank_account!.bank_account_type,
                  }
                : undefined,
              type: ESplitPaymentType.BENEFICIARY,
            },
          ]);
        }
      });

      if (hasApplication.funding?.id) {
        setSelectedFunding(() => hasApplication.funding!.id!);
      }

      setSimulationV2Request({
        requestedAmount: hasApplication.requested_amount,
        numPayments: hasApplication.num_payments,
        interestRate: hasApplication.interest_rate,
        financeFee: hasApplication.finance_fee,
        firstPaymentDate: hasApplication.first_payment_date,
        disbursementDate: hasApplication.disbursement_date,
        tacAmount: hasApplication.tac_amount,
      } as TSimulatorV2Request);
    }
  }, [hasApplication]);

  useEffect(() => {
    if (!hasMultiplesFundings && !hasApplication && userInfo) {
      setSelectedFunding(userInfo.fundings[0]);
    }
  }, [hasMultiplesFundings]);

  const steps = useMemo(() => {
    const stepsList = getApplicationSteps(
      !prevProduct.current,
      product,
      !!fgtsQueryBalanceCheckId,
    );

    return stepsList
      ?.filter((step) => {
        if (step.title === 'Originador' && !is_issuer_app) return false;
        if (
          step.title === 'Custodiante' &&
          product?.type_product_emission !==
            TypeProductEmission.COMMERCIAL_PAPER
        )
          return false;
        if (
          step.title === 'Template de CCB' &&
          !product?.custom_ccb_template_required
        )
          return false;
        if (
          step.title === 'Split de Pagamento' &&
          product?.beneficiary_type !== 'SPLIT_DISBURSEMENT'
        )
          return false;
        if (
          step.title === 'Boletos para desembolso' &&
          product?.type_product_emission !== TypeProductEmission.BILLET_PAYMENT
        )
          return false;
        if (
          step.title === 'Pix Copia e Cola' &&
          product?.type_product_emission !== TypeProductEmission.QRCODE_PIX
        )
          return false;
        if (step.title === 'Funding' && !hasMultiplesFundings) return false;
        if (
          step.title === 'Conta Beneficiária' &&
          product?.beneficiary_type !==
            EBeneficiaryType.EXTERNAL_REGISTERED_ACCOUNT
        )
          return false;
        return true;
      })
      .map((step) => {
        if (step.title === 'Valores da Solicitação' && isNewSimulator) {
          return {
            ...step,
            component: <SimulationV2Step />,
          };
        }
        return step;
      });
  }, [
    product,
    hasMultiplesFundings,
    is_issuer_app,
    isConsigned,
    isNewSimulator,
    fgtsQueryBalanceCheckId,
  ]);

  const hasVariablesStep = useMemo(
    () => steps.some((step) => step.title === 'Campos adicionais'),
    [steps],
  );

  const onForward = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, steps.length]);

  const onBackTwoSteps = useCallback(() => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 2);
    }
  }, [activeStep]);

  const onBack = useCallback(() => {
    if (activeStep > 0) {
      if (steps[activeStep]?.title === SIMULATION_V2_STEP_TITLE) {
        setLoanGuaranteeFormValues(defaultLoanGuaranteeFormValues);
        setSimulationPersistedNumPayments(null);
      }
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, steps]);

  const renderSteps = () => {
    return steps[activeStep].component;
  };

  const simulateWithDifferentDisbursementDate = useCallback(
    async (disbursementDate: string) => {
      if (!product?.id || !simulationV2Request) {
        return;
      }

      try {
        const formattedDate = format(new Date(disbursementDate), 'yyyy-MM-dd');

        const response = await simulateV2(product.id, {
          ...simulationV2Request,
          disbursementDate: formattedDate,
        });

        if (response) {
          setSimulationV2(response);
          setSimulationV2Request(simulationV2Request);
          setTabSimulationIndex(tabSimulationIndex || '0');
          if (response?.financed_amount && selectedFunding) {
            getBasePremiumAmount(
              response.financed_amount,
              selectedFunding,
            ).then((res) => setBasePremiumAmount(res.amount));
          }
        }
      } catch (error) {
        console.error('Erro ao simular com data diferente:', error);
      }
    },
    [
      simulationV2Request,
      product,
      simulateV2,
      tabSimulationIndex,
      selectedFunding,
      setSimulationV2,
      setSimulationV2Request,
      setTabSimulationIndex,
      getBasePremiumAmount,
      setBasePremiumAmount,
    ],
  );

  useEffect(() => {
    if (typeof location.state?.product === 'object') {
      setProduct(location.state?.product);
      history.replaceState({}, document.title);
    }
  }, []);

  return (
    <ApplicationRequestContext.Provider
      value={{
        setTacValidationError,
        tacValidationError,
        activeStep,
        onBack,
        onBackTwoSteps,
        onForward,

        hasCustomVariables,
        setHasCustomVariables,

        borrowerData,
        setBorrowerData,

        product,
        setProduct,

        customVariables,
        setCustomVariables,

        subscribers,
        setSubscribers,

        borrowerQualification,
        setBorrowerQualification,

        beneficiaryAccount,
        setBeneficiaryAccount,

        funding: selectedFunding,
        hasMultiplesFundings: !!hasMultiplesFundings,
        setFunding: setSelectedFunding,

        custodian,
        setCustodian,

        basePremiumAmount,

        simulationV2,
        simulationV2Request,
        setSimulationV2: (
          simulationResponse,
          simulationRequestData,
          tabIndex,
        ) => {
          setSimulationV2(simulationResponse);
          setSimulationV2Request(simulationRequestData);
          setTabSimulationIndex(tabIndex);
          if (simulationResponse?.financed_amount && selectedFunding) {
            getBasePremiumAmount(
              simulationResponse.financed_amount,
              selectedFunding,
            ).then((res) => setBasePremiumAmount(res.amount));
          }
        },

        simulateWithDifferentDisbursementDate,

        billetValidationResponse,
        setBilletValidationResponse,

        qrcodeEmvValidationResponse,
        setQrcodeEmvValidationResponse,

        isClonning,

        originator: selectedOriginator,
        updateOriginator: (originatorId: string | null) => {
          setSelectedOriginator(originatorId);
        },

        consignedList,
        setConsignedList,
        consignedConfig,
        setConsignedConfig,
        consignedProduct,
        setConsignedProduct,
        consignedError,
        setConsignedError,
        setFgtsPeriods,
        fgtsPeriods,
        fgtsValidationErrors,
        setFgtsValidationErrors,
        fgtsQueryBalanceCheckId,
        setFgtsQueryBalanceCheckId,
        loanGuaranteeInfo,
        setLoanGuaranteeInfo,
        loanGuaranteeFormValues,
        setLoanGuaranteeFormValues,
        simulationPersistedNumPayments,
        setSimulationPersistedNumPayments,

        splitPayments,
        setSplitPayments,

        clearProductSelection,

        setCobransaasResponse,
        cobransaasResponse,

        customCcbTemplateId,
        setCustomCcbTemplateId,

        hasVariablesStep,
        getElegibilityRules,
        elegibilityError,
        setElegibilityError,
        elegibilities,
        setElegibilities,
      }}
    >
      <ApplicationRequestContainer>
        <ApplicationRequestStepperContainer>
          <Stepper
            key={`${steps.length}-${fgtsQueryBalanceCheckId ?? 'none'}`}
            activeStep={activeStep}
            stepsItems={steps.map((step, inx) => ({
              step: inx,
              title: step.title,
              clickable: false,
            }))}
          />
        </ApplicationRequestStepperContainer>
        <ApplicationRequestStepContainer>
          {renderSteps()}
        </ApplicationRequestStepContainer>
      </ApplicationRequestContainer>
    </ApplicationRequestContext.Provider>
  );
};

export default ApplicationRequest;
